// List display preference for Apple TV clone surfaces.
//
// grid     — portrait poster cards in a wrapped grid
// list     — one title per row with poster + metadata
// backdrop — landscape backdrop cards in a wrapped grid
//
// Persisted in localStorage and broadcast via window events so mounted
// list/grid components can react live when the preference changes.

const LEGACY_POSTER_KEY = "curator:applePosterLayout";
const LIST_DISPLAY_KEY = "curator:appleListDisplay";
const BODY_BACKDROP_CLASS = "apple-poster-backdrop";
const BODY_LIST_CLASS = "apple-list-display-list";

export const POSTER_LAYOUT_VERTICAL = "vertical";
export const POSTER_LAYOUT_HORIZONTAL = "horizontal";
export const POSTER_LAYOUT_EVENT = "curator:posterLayoutChange";

export const LIST_DISPLAY_GRID = "grid";
export const LIST_DISPLAY_LIST = "list";
export const LIST_DISPLAY_BACKDROP = "backdrop";
export const LIST_DISPLAY_EVENT = "curator:listDisplayChange";

export const LIST_DISPLAY_MODES = [
  {
    id: LIST_DISPLAY_GRID,
    name: "Grid",
    description: "Portrait posters in a dense grid.",
  },
  {
    id: LIST_DISPLAY_LIST,
    name: "List",
    description: "One title per row with score.",
  },
  {
    id: LIST_DISPLAY_BACKDROP,
    name: "Backdrops",
    description: "Wide art with titles beneath.",
  },
];

/** @deprecated Use LIST_DISPLAY_MODES */
export const POSTER_LAYOUTS = [
  {
    id: POSTER_LAYOUT_VERTICAL,
    name: "Posters",
    description: "Portrait poster art, like the App Store.",
  },
  {
    id: POSTER_LAYOUT_HORIZONTAL,
    name: "Backdrops",
    description: "Wide backdrop art with the title beneath each card.",
  },
];

function posterLayoutForDisplay(mode) {
  return mode === LIST_DISPLAY_BACKDROP ? POSTER_LAYOUT_HORIZONTAL : POSTER_LAYOUT_VERTICAL;
}

function displayForPosterLayout(layout) {
  return layout === POSTER_LAYOUT_HORIZONTAL ? LIST_DISPLAY_BACKDROP : LIST_DISPLAY_GRID;
}

export function getStoredListDisplay() {
  try {
    const stored = window.localStorage.getItem(LIST_DISPLAY_KEY);
    if (stored === LIST_DISPLAY_GRID || stored === LIST_DISPLAY_LIST || stored === LIST_DISPLAY_BACKDROP) {
      return stored;
    }
    const legacy = window.localStorage.getItem(LEGACY_POSTER_KEY);
    if (legacy === POSTER_LAYOUT_HORIZONTAL) return LIST_DISPLAY_BACKDROP;
    if (legacy === POSTER_LAYOUT_VERTICAL) return LIST_DISPLAY_GRID;
  } catch (_err) {
    // Storage unavailable.
  }
  return LIST_DISPLAY_GRID;
}

export function getStoredPosterLayout() {
  return posterLayoutForDisplay(getStoredListDisplay());
}

export function applyPosterLayoutClass(layout) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle(BODY_BACKDROP_CLASS, layout === POSTER_LAYOUT_HORIZONTAL);
}

export function applyListDisplayClasses(mode) {
  if (typeof document === "undefined") return;
  const display = mode === LIST_DISPLAY_LIST || mode === LIST_DISPLAY_BACKDROP
    ? mode
    : LIST_DISPLAY_GRID;
  document.body.classList.toggle(BODY_LIST_CLASS, display === LIST_DISPLAY_LIST);
  applyPosterLayoutClass(posterLayoutForDisplay(display));
}

function persistListDisplay(mode) {
  try {
    window.localStorage.setItem(LIST_DISPLAY_KEY, mode);
    window.localStorage.setItem(LEGACY_POSTER_KEY, posterLayoutForDisplay(mode));
  } catch (_err) {
    // Storage unavailable; preference still applies this session.
  }
}

function dispatchListDisplayChange(mode) {
  try {
    window.dispatchEvent(new CustomEvent(LIST_DISPLAY_EVENT, { detail: mode }));
    window.dispatchEvent(new CustomEvent(POSTER_LAYOUT_EVENT, {
      detail: posterLayoutForDisplay(mode),
    }));
  } catch (_err) {
    // CustomEvent unsupported; mounted cards pick it up on next mount.
  }
}

export function setListDisplay(mode) {
  const next = mode === LIST_DISPLAY_LIST || mode === LIST_DISPLAY_BACKDROP
    ? mode
    : LIST_DISPLAY_GRID;
  persistListDisplay(next);
  applyListDisplayClasses(next);
  dispatchListDisplayChange(next);
  return next;
}

export function setPosterLayout(layout) {
  return setListDisplay(displayForPosterLayout(
    layout === POSTER_LAYOUT_HORIZONTAL ? POSTER_LAYOUT_HORIZONTAL : POSTER_LAYOUT_VERTICAL,
  ));
}

export function subscribeListDisplay(callback) {
  if (typeof window === "undefined") return () => {};
  const handler = (event) => callback(event?.detail || getStoredListDisplay());
  window.addEventListener(LIST_DISPLAY_EVENT, handler);
  return () => window.removeEventListener(LIST_DISPLAY_EVENT, handler);
}

export function subscribePosterLayout(callback) {
  if (typeof window === "undefined") return () => {};
  const handler = (event) => callback(event?.detail || getStoredPosterLayout());
  window.addEventListener(POSTER_LAYOUT_EVENT, handler);
  return () => window.removeEventListener(POSTER_LAYOUT_EVENT, handler);
}
